import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, AlertCircle, TrendingUp, TrendingDown, Minus,
  CalendarClock, Sparkles, BookOpen, Building2,
} from 'lucide-react';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Lightweight markdown → HTML for the executive summary sections.
// Trust boundary: this content was authored by a logged-in L&D admin
// for sharing with their CFO/board. We still escape HTML to prevent
// any accidental script injection from GPT output.
const renderMarkdown = (md) => {
  if (!md) return '';
  let s = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  s = s.replace(/^### (.*)$/gm, '<h3 class="text-base font-semibold text-slate-900 mt-4 mb-1.5">$1</h3>');
  s = s.replace(/^## (.*)$/gm, '<h2 class="text-lg font-bold text-slate-900 mt-5 mb-2">$1</h2>');
  s = s.replace(/^# (.*)$/gm, '<h1 class="text-xl font-bold text-slate-900 mt-5 mb-2">$1</h1>');
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
  s = s.replace(/^- (.*)$/gm, '<li class="ml-5 list-disc text-slate-700">$1</li>');
  s = s.replace(/(<li[^>]*>.*?<\/li>(\s*<li[^>]*>.*?<\/li>)*)/gs,
    '<ul class="space-y-1 my-2">$1</ul>');
  s = s.replace(/\n\n+/g, '</p><p class="text-slate-700 leading-relaxed mb-2">');
  return `<p class="text-slate-700 leading-relaxed mb-2">${s}</p>`;
};

const DeltaPill = ({ delta }) => {
  if (delta == null) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
        <Minus className="h-3.5 w-3.5" />
        No baseline
      </span>
    );
  }
  const positive = delta > 0;
  const flat = delta === 0;
  const cls = flat
    ? 'bg-slate-100 text-slate-600'
    : positive
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-rose-50 text-rose-700';
  const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {positive ? '+' : ''}{Number(delta).toFixed(2)}
    </span>
  );
};

/**
 * Chrome-free, read-only public preview of an Executive Report Card.
 * No auth. Signed token in URL. Designed for CHROs to share with their
 * CFO / board / stakeholders. Tracks anonymous CTA clicks for engagement
 * analytics.
 */
export default function ReportCardSharePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [ctaSent, setCtaSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/people-engine/public/report-card/${token}`);
        if (!cancelled) setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        const detail =
          err?.response?.status === 410
            ? 'This share link has expired or been revoked. Please request a fresh one.'
            : err?.response?.status === 404
              ? 'This share link is invalid.'
              : err?.response?.data?.detail || 'Could not load this report card.';
        if (!cancelled) setState({ loading: false, error: detail, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const fireCtaClick = async () => {
    if (ctaSent) return;
    setCtaSent(true);
    try {
      await axios.post(`${BACKEND}/api/people-engine/public/report-card/${token}/event`,
        { kind: 'cta_click' });
    } catch (_) { /* analytics best-effort */ }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading report card…</span>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="share-error">
          <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-rose-500" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-slate-600">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data || {};
  const ba = d.before_after || {};
  const formatPeriod = (p) => p || '—';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="share-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {d.organization_logo_url ? (
              <img src={d.organization_logo_url} alt={d.organization_name}
                className="h-9 w-9 rounded-md object-contain bg-slate-50 border border-slate-100" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-indigo-600 text-white flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-medium">Executive Report Card</p>
              <p className="text-sm font-semibold text-slate-900 truncate">{d.organization_name}</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400">
            <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
            <span>Powered by Seertech</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Title */}
        <section>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight" data-testid="share-title">
            {d.title || 'Untitled Report Card'}
          </h1>
          {d.description && (
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-slate-500">
            {d.skill_name && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                <BookOpen className="h-3.5 w-3.5" />
                {d.skill_name}
              </span>
            )}
            {d.created_at && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5" />
                {new Date(d.created_at).toLocaleDateString(undefined,
                  { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </section>

        {/* Headline metrics — journey chain for Missing Middle briefs,
            before/after for skill report cards, hidden otherwise */}
        {d.journey?.stages?.length ? (
          <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="share-journey-chain">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
              Journey Conversion Chain{d.journey.period ? ` · ${d.journey.period}` : ''}
            </p>
            <div className="flex items-stretch gap-1 overflow-x-auto">
              {d.journey.stages.map((s, i) => (
                <React.Fragment key={s.label || i}>
                  {i > 0 && (
                    <div className="flex items-center px-0.5 flex-shrink-0">
                      <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                        (s.conversion_delta || 0) < 0
                          ? 'text-rose-700 bg-rose-50 border-rose-200'
                          : 'text-emerald-700 bg-emerald-50 border-emerald-200'
                      }`}>
                        {(s.conversion_delta || 0) > 0 ? '+' : (s.conversion_delta || 0) < 0 ? '−' : ''}
                        {Math.abs(Math.round((s.conversion_delta || 0) * 1000) / 10)}pp
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-[92px] rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-center">
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{Math.round((s.score || 0) * 100)}%</p>
                    <p className="text-[10px] text-slate-500 font-medium mt-0.5">{s.label}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </section>
        ) : (ba.before_avg_achieved != null || ba.after_avg_achieved != null) ? (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="share-metrics">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Before</p>
            <p className="text-2xl font-bold text-slate-700 tabular-nums mt-1">
              {ba.before_avg_achieved == null ? '—' : Number(ba.before_avg_achieved).toFixed(2)}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              avg over {ba.before_periods ?? 0} period{ba.before_periods === 1 ? '' : 's'}
            </p>
          </div>
          <div className="bg-white rounded-xl border-2 border-indigo-200 p-4">
            <p className="text-[10px] uppercase tracking-wider text-indigo-500 font-semibold">After</p>
            <p className="text-2xl font-bold text-indigo-900 tabular-nums mt-1">
              {ba.after_avg_achieved == null ? '—' : Number(ba.after_avg_achieved).toFixed(2)}
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              avg over {ba.after_periods ?? 0} period{ba.after_periods === 1 ? '' : 's'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Delta</p>
            <div className="mt-1.5">
              <DeltaPill delta={ba.delta} />
            </div>
            <p className="text-[11px] text-slate-400 mt-auto pt-2">
              anchored at {formatPeriod(ba.anchor_period)}
            </p>
          </div>
        </section>
        ) : null}

        {/* Sections (markdown) */}
        {(d.sections || []).map((s, i) => (
          <section key={s.id || i} className="bg-white rounded-xl border border-slate-200 p-5"
            data-testid={`share-section-${i}`}>
            <h2 className="text-base font-bold text-slate-900 mb-2">{s.title}</h2>
            <div className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(s.content) }} />
          </section>
        ))}

        {/* CTA */}
        <section className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 sm:p-8 text-white shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold">Want this kind of visibility for your org?</h3>
              <p className="text-sm text-indigo-100 mt-1">
                Seertech ties every dollar of L&amp;D spend to a measurable business KPI.
              </p>
            </div>
            <a
              href="https://www.seertechsolutions.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              onClick={fireCtaClick}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white text-indigo-700 font-semibold text-sm hover:bg-indigo-50 transition-colors shadow-sm whitespace-nowrap"
              data-testid="share-cta"
            >
              Talk to L&amp;D
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-xs text-slate-400 pt-4 pb-8">
          {d.expires_at && (
            <p>This link expires {new Date(d.expires_at).toLocaleDateString(undefined,
              { year: 'numeric', month: 'short', day: 'numeric' })}.</p>
          )}
          <p className="mt-1">Read-only preview · Powered by Seertech</p>
        </footer>
      </main>
    </div>
  );
}
